//--- React
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
//--- Ant Design
import { Avatar, Card, Spin } from "antd";
//--- Project
import { getCompanyReviews } from "apis/competitorPulseApis";
import APIError from "components/Common/APIError";
import NoData from "components/Common/NoData";
//--- Icon
import { TagsOutlined, UserOutlined } from "@ant-design/icons";
import "./styles.css";

const reviewTextMaxLength = 320;
const reviewCategoryMaxLength = 6;

const HighlightText = ({ text, indexToHighlight, color }) => {
  const highlightRanges = indexToHighlight.flatMap(
    ({ sub_category, chunk_ranges }) =>
      chunk_ranges.map(({ chunk_start_index, chunk_end_index }) => ({
        start: chunk_start_index,
        end: chunk_end_index,
        bgcolor: color[sub_category] || "transparent",
      }))
  );

  return (
    <span className="review-text">
      {text.split("").map((char, index) => {
        const range = highlightRanges.find(
          (r) => index >= r.start && index <= r.end
        );

        return (
          <span
            key={index}
            style={{
              color:
                range?.bgcolor && range.bgcolor !== "transparent"
                  ? "#ffffff"
                  : "unset",
              backgroundColor: range ? range.bgcolor : "transparent",
            }}
          >
            {char}
          </span>
        );
      })}
    </span>
  );
};

const ReviewList = ({ filterValue, subCatgryColor, setReviewCount }) => {
  const [reviewDetails, setReviewDetails] = useState({
    list: [],
    totalElements: 0,
  });
  const [paginationDetails, setPaginationDetails] = useState({
    current: 1,
    pageSize: 3,
  });
  const [viewReviews, setViewReviews] = useState({});

  useMemo(() => {
    setPaginationDetails({
      current: 1,
      pageSize: 3,
    });
    setViewReviews({});
    // eslint-disable-next-line
  }, [filterValue.company]);

  const {
    data: reviewData,
    isLoading: reviewDataLoading,
    isError: reviewDataIsError,
    error: reviewDataError,
  } = useQuery({
    queryKey: [
      "reviewDetails",
      filterValue.startDate,
      filterValue.endDate,
      filterValue.sourceList,
      filterValue.bikeModel,
      filterValue.sentiment,
      filterValue.category,
      filterValue.subCategory,
      paginationDetails.pageSize,
    ],
    queryFn: () =>
      getCompanyReviews(
        filterValue.company,
        Math.floor(filterValue.startDate / 1000),
        Math.floor(filterValue.endDate / 1000),
        paginationDetails.current,
        paginationDetails.pageSize,
        filterValue.sourceList,
        filterValue.bikeModel,
        filterValue.sentiment,
        filterValue.category,
        filterValue.subCategory
      ),
    keepPreviousData: true,
    enabled: !!(
      filterValue.company &&
      filterValue.startDate &&
      filterValue.endDate
    ),
    refetchOnWindowFocus: false,
  });

  useMemo(() => {
    if (reviewData) {
      setReviewDetails({
        list: reviewData?.reviews,
        totalElements: reviewData?.total_reviews,
      });
      setReviewCount(reviewData?.total_reviews);
    } else {
      setReviewCount(0);
    }
  }, [reviewData, setReviewDetails, setReviewCount]);

  const onLoadReviews = () => {
    setPaginationDetails((prev) => ({ ...prev, pageSize: prev.pageSize + 3 }));
  };

  const onSelectReview = (id, name) => {
    setViewReviews((prev) => ({
      ...prev,
      [id]: { ...prev[id], [name]: !prev?.[id]?.[name] },
    }));
  };

  return (
    <Card
      title={<div className="sub-header">Reviews</div>}
      bordered={false}
      className="review-card"
    >
      <Spin
        className="fs1"
        spinning={reviewDataLoading}
        tip="Loading..."
        size="large"
      >
        {reviewDataIsError ? (
          <div className="review-box">
            <APIError message={reviewDataError?.response?.data?.message} />
          </div>
        ) : reviewDetails?.list?.length > 0 ? (
          <>
            {reviewDetails?.list?.map((review, index) => (
              <div className="review-box" key={index}>
                <div className="flex-between mb05">
                  <div>
                    <div className="title">
                      <Avatar
                        style={{
                          fontSize: "14px",
                        }}
                        className="mr05"
                      >
                        <UserOutlined />
                      </Avatar>
                      {review?.user_name}
                    </div>
                    <div className="sub-title">
                      Source: Review in {review?.app} on{" "}
                      {dayjs(review?.date * 1000).format("DD MMM YYYY")}
                    </div>
                    <div className="sub-title">Model: {review?.bike_model}</div>
                  </div>
                </div>
                <HighlightText
                  text={
                    review?.review_text?.length > reviewTextMaxLength &&
                    !viewReviews?.[review.review_id]?.text
                      ? review?.review_text.slice(0, reviewTextMaxLength)
                      : review?.review_text
                  }
                  indexToHighlight={review?.chunks?.flatMap(
                    (item) => item?.sub_categories
                  )}
                  color={subCatgryColor}
                />
                {review?.review_text?.length > reviewTextMaxLength &&
                  !viewReviews?.[review.review_id]?.text && (
                    <span
                      className="more-link"
                      onClick={() => onSelectReview(review?.review_id, "text")}
                    >
                      {"  "}
                      Read more...
                    </span>
                  )}
                <div className="mt05 category-label">
                  <TagsOutlined className="category-icon" />
                  <span>
                    <span className="ml05 category-name">
                      {review?.categories?.length > reviewCategoryMaxLength &&
                      !viewReviews?.[review.review_id]?.category
                        ? review?.categories
                            ?.slice(0, reviewCategoryMaxLength)
                            .join(", ")
                        : review?.categories?.join(", ")}{" "}
                    </span>
                    {review?.categories?.length > reviewCategoryMaxLength &&
                      !viewReviews?.[review.review_id]?.category && (
                        <span
                          onClick={() =>
                            onSelectReview(review?.review_id, "category")
                          }
                          className="more-link"
                        >
                          {" "}
                          View more...
                        </span>
                      )}
                  </span>
                </div>
              </div>
            ))}
            {paginationDetails.pageSize < reviewDetails?.totalElements && (
              <div className="flex-center">
                <div onClick={onLoadReviews} className="load-more-btn">
                  Load more reviews
                </div>
              </div>
            )}
          </>
        ) : (
          <NoData />
        )}
      </Spin>
    </Card>
  );
};

export default ReviewList;
